import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const FineList = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [balance, setBalance] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactionType, setTransactionType] = useState(""); // State for the transaction type filter
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BASE_URL}fine/transactions`)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 1) {
          setTransactions(data.transactions);
          setFilteredTransactions(data.transactions);
        } else {
          alert("Failed to fetch fine transactions.");
        }
      })
      .catch((error) =>
        console.error("Error fetching fine transactions:", error)
      );

    // Fetch balance - try multiple endpoints
    const fetchBalance = async () => {
      const endpoints = [
        `${BASE_URL}fine/balance?staff_id=1`,
        `${BASE_URL}fine/balance`,
        `${BASE_URL}fine`,
      ];

      for (let endpoint of endpoints) {
        try {
          const response = await fetch(endpoint);
          const data = await response.json();

          if (data.status === 1) {
            // Try different possible balance field names
            const balanceValue =
              data.balance ||
              data.data?.balance ||
              data.data ||
              data.total_balance;
            if (balanceValue !== undefined) {
              setBalance(balanceValue);
              return;
            }
          }
        } catch (error) {
          console.log(`Balance endpoint ${endpoint} failed:`, error);
        }
      }
      // If all endpoints fail, calculate from transactions
      calculateBalanceFromTransactions();
    };

    fetchBalance();
  }, []);

  // Calculate balance from transactions if API fails
  const calculateBalanceFromTransactions = () => {
    if (transactions.length === 0) return 0;

    const calculatedBalance = transactions.reduce((total, transaction) => {
      const amount = parseFloat(transaction.amount) || 0;
      if (transaction.transaction_type === "Incoming") {
        return total + amount;
      } else if (transaction.transaction_type === "Outgoing") {
        return total - amount;
      }
      return total;
    }, 0);

    setBalance(calculatedBalance);
  };

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    filterTransactions(term, startDate, endDate, transactionType);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Filter transactions based on search term, date range, and transaction type
  const filterTransactions = (term, start, end, type) => {
    let filtered = transactions.filter((transaction) => {
      const matchesSearch =
        transaction.staff_name.toLowerCase().includes(term.toLowerCase()) ||
        transaction.transaction_type.toLowerCase().includes(term.toLowerCase());

      const transactionDate = new Date(transaction.created_at)
        .toISOString()
        .split("T")[0];
      const matchesDateRange =
        (!start || transactionDate >= start) &&
        (!end || transactionDate <= end);

      const matchesTransactionType = type
        ? transaction.transaction_type.toLowerCase() === type.toLowerCase()
        : true;

      return matchesSearch && matchesDateRange && matchesTransactionType;
    });
    setFilteredTransactions(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleAdd = () => {
    navigate("/AddFine");
  };

  const handleAdds = () => {
    navigate("/AddsFine");
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      fetch(`${BASE_URL}fine/transaction/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 1) {
            alert(`Transaction ${id} deleted successfully!`);
            setTransactions(
              transactions.filter((transaction) => transaction.id !== id)
            );
            setFilteredTransactions(
              filteredTransactions.filter(
                (transaction) => transaction.id !== id
              )
            );
          } else {
            alert("Failed to delete transaction.");
          }
        })
        .catch((error) => {
          console.error("Error deleting transaction:", error);
          alert("There was an error deleting the transaction.");
        });
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Fine Transactions", 14, 10);
    const tableData = filteredTransactions.map((transaction) => [
      transaction.staff_name || "N/A",
      transaction.amount || "N/A",
      transaction.transaction_type || "N/A",
      transaction.description || "N/A",
      new Date(transaction.created_at).toLocaleString(),
    ]);
    doc.autoTable({
      head: [
        ["Staff Name", "Amount", "Transaction Type", "Description", "Date"],
      ],
      body: tableData,
    });
    doc.save("fine_transactions.pdf");
  };

  const handleExportExcel = () => {
    const tableData = filteredTransactions.map((transaction) => ({
      "Staff Name": transaction.staff_name || "N/A",
      Amount: transaction.amount || "N/A",
      "Transaction Type": transaction.transaction_type || "N/A",
      Description: transaction.description || "N/A",
      Date: new Date(transaction.created_at).toLocaleString() || "N/A",
    }));
    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fine Transactions");
    XLSX.writeFile(wb, "fine_transactions.xlsx");
  };

  const handleDateFilter = () => {
    filterTransactions(searchTerm, startDate, endDate, transactionType);
  };

  const handleTransactionTypeChange = (event) => {
    setTransactionType(event.target.value);
    filterTransactions(searchTerm, startDate, endDate, event.target.value);
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Fine Wallet</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-4">
                    <h5>
                      TOTAL GRAM:{" "}
                      {balance !== null ? ` ${balance}` : "Loading..."}
                    </h5>
                  </div>
                  {/* <div className="col-md-2 text-end"><button className="btn btn-primary" onClick={handleAdd}>Payment</button><br></br><br></br></div> */}
                  {/* <div className="col-md-2 text-end"><button className="btn btn-primary" onClick={handleAdds}>Received</button><br></br><br></br></div> */}
                  <div className="col-md- text-end">
                    <button
                      className="btn btn-secondary ms-3"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="mb-6 row">
                  <div className="col-md-3">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search by staff name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) =>
                        handleDateChange(e.target.value, endDate)
                      }
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) =>
                        handleDateChange(startDate, e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="transactionType" className="form-label">
                      Transaction Type
                    </label>
                    <select
                      id="transactionType"
                      className="form-control"
                      value={transactionType}
                      onChange={handleTransactionTypeChange}
                    >
                      <option value="">All</option>
                      <option value="Outgoing">Outgoing</option>
                      <option value="Incoming">Incoming</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="endDate" className="form-label mb-3">
                      <br></br>
                      <br></br>
                    </label>
                    <button
                      className="btn btn-secondary"
                      onClick={handleDateFilter}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>STAFF NAME</th>
                        <th>GRAM</th>
                        <th>TRANSACTION TYPE</th>
                        <th>DESCRIPTION</th>
                        <th>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.length > 0 ? (
                        filteredTransactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td>{transaction.staff_name || "N/A"}</td>
                            <td>{transaction.amount || "N/A"}</td>
                            <td>{transaction.transaction_type || "N/A"}</td>
                            <td>{transaction.description || "N/A"}</td>
                            <td>{formatDate(transaction.created_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No Wallet found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FineList;
